import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { ItalianAddressVM } from 'practice/view-models/practice-detail';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import ListCategory from 'practice/components/shared/list-category';
import { DatePicker } from '@mui/lab';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useParams } from 'react-router-dom';
import { TypeRegister } from 'practice/constants/type-register';
import {
  HouseOccupationType,
  TipoOccupazioneLegittima,
  TIPO_OCCUPAZIONE_LEGITTIMA_LABELS,
} from 'practice/constants/house-occupation';

interface FormProps {
  disabled?: boolean;
  onChange: (key: keyof ItalianAddressVM, value: ItalianAddressVM[keyof ItalianAddressVM]) => void;
  values?: ItalianAddressVM;
}
const FormComponent = ({ disabled, onChange, values }: FormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const paramsUrl = useParams();

  const onChangeCategory = useEventCallback((cat: SelectChangeEvent<string>) => {
    onChange('cat', cat.target.value);
  });

  /*
  const isASSDatiCatastali = useMemo(() => {
    return Boolean(
      values?.sheet === 'ASS' || values?.particle === 'ASSENTE' || values?.sub === 'ASS'
    );
  }, [values]);
  */
  const codeSenzaFissaDimora = userProfile.datiLogin?.profile?.feature?.senzaFissaDimora || '';

  const isCambioResidenza = useMemo(() => {
    if (paramsUrl.tipoRegistro === TypeRegister.Residence) {
      return true;
    }

    return false;
  }, [paramsUrl.tipoRegistro]);

  const isImmigrazione = useMemo(() => {
    if (paramsUrl.tipoRegistro === TypeRegister.Immigration) {
      return true;
    }

    return false;
  }, [paramsUrl.tipoRegistro]);

  const tipoOccupazioneLegittima = values?.occupantiLegittimi;
  const tipoOccupazioneLegittimaLabel =
    values?.houseOccupationType === HouseOccupationType.OccupanteLegittimo &&
    tipoOccupazioneLegittima
      ? TIPO_OCCUPAZIONE_LEGITTIMA_LABELS[tipoOccupazioneLegittima as TipoOccupazioneLegittima]
      : undefined;

  const hasAssensoOspitante =
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteProprietario ||
    tipoOccupazioneLegittima ===
      TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario ||
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteIntestatarioERP ||
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate;

  const isERP =
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.AutorizzatoERP ||
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteIntestatarioERP;

  const isOspitante =
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteIntestatarioERP ||
    tipoOccupazioneLegittima ===
      TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario;

  const isStruttura =
    tipoOccupazioneLegittima === TipoOccupazioneLegittima.OspiteETSStrutturaSanitariaOPrivate;

  const isProprietario = [
    TipoOccupazioneLegittima.AffittuarioNonAgenziaEntrate,
    TipoOccupazioneLegittima.ComodatariNonAgenziaEntrate,
    TipoOccupazioneLegittima.OspiteProprietario,
    TipoOccupazioneLegittima.OspiteIntestatarioContrattoLocazioneComodatarioUsufruttuario,
  ].includes(tipoOccupazioneLegittima as TipoOccupazioneLegittima);

  const showERP = isERP && Boolean(values?.fiscalCodeERP || values?.enteERP || values?.pecEnte);
  const showOspitante =
    isOspitante &&
    Boolean(
      values?.fiscalCodeOspitante ||
        values?.surnameOspitante ||
        values?.nameOspitante ||
        values?.birthDateOspitante ||
        values?.municipalityResidenceOspitante ||
        values?.addressOspitante
    );
  const showStruttura =
    isStruttura &&
    Boolean(
      values?.fiscalCodeStruttura ||
        values?.nameStruttura ||
        values?.municipalityResidenceStruttura ||
        values?.addressStruttura ||
        values?.pecEnteStruttura
    );
  const showProprietario =
    isProprietario &&
    Boolean(
      values?.fiscalCodeProprietario ||
        values?.surnameProprietario ||
        values?.nameProprietario ||
        values?.municipalityResidenceProprietario ||
        values?.addressProprietario
    );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Codice Via"
          label="Codice Via"
          id="codeStreet"
          name="codeStreet"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.codeStreet || ''}
          onChange={(event) => onChange('codeStreet', event.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          aria-label="Via"
          label="Via"
          id="street"
          name="street"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.street || ''}
          onChange={(event) => onChange('street', event.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="civicNumber"
          aria-label="NR Civico"
          label="NR Civico"
          fullWidth
          value={values?.civicNumber || ''}
          onChange={(event) => {
            onChange('civicNumber', event.target.value);
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={false} md={1}>
        <Typography align="center" fontSize="2rem">
          /
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          id="exponent"
          aria-label="Esponente"
          label="Esponente"
          fullWidth
          value={values?.exponent || ''}
          onChange={(event) => {
            onChange('exponent', event.target.value);
          }}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Città"
          label="Città"
          id="municipality"
          name="municipality"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.municipality || ''}
          onChange={(event) => onChange('municipality', event.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          aria-label="Provincia"
          label="Provincia"
          id="province"
          name="province"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.province || ''}
          onChange={(event) => onChange('province', event.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          aria-label="Cap"
          label="Cap"
          id="cap"
          name="cap"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.cap || ''}
          onChange={(event) => onChange('cap', event.target.value)}
          disabled={disabled}
        />
      </Grid>
      {(codeSenzaFissaDimora === '1' || codeSenzaFissaDimora === '2') &&
      (isCambioResidenza || isImmigrazione) ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isHomeless"
                checked={values?.isHomeless || false}
                onChange={() => onChange('isHomeless', !values?.isHomeless)}
                inputProps={{ 'aria-label': 'Senza fissa dimora' }}
                disabled={disabled}
              />
            }
            label="Senza fissa dimora"
          />
        </Grid>
      ) : undefined}

      <Grid item xs={12} md={8}>
        <PmiTextField
          id="type-occupation"
          label="Tipo occupazione abitazione"
          aria-label="Tipo occupazione abitazione"
          fullWidth
          disabled
          value={values?.houseOccupationDecType || ''}
        />
      </Grid>

      {tipoOccupazioneLegittimaLabel ? (
        <>
          <Grid item xs={12} md={8}>
            <PmiTextField
              id="legitimate-occupation-type"
              label="Tipo occupazione legittima"
              aria-label="Tipo occupazione legittima"
              fullWidth
              disabled
              value={tipoOccupazioneLegittimaLabel}
            />
          </Grid>
          {hasAssensoOspitante ? (
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="assensoOspitante"
                    checked={values?.assensoOspitante || false}
                    inputProps={{ 'aria-label': 'Assenso Ospitante' }}
                    disabled
                  />
                }
                label="Assenso Ospitante"
              />
            </Grid>
          ) : undefined}
        </>
      ) : undefined}

      {values?.withDatiAgenzia ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati agenzia entrate </PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={5}>
            <PmiTextField
              id="site"
              label="Sede"
              aria-label="Sede"
              fullWidth
              disabled={disabled}
              value={values?.site || ''}
              onChange={(event) => onChange('site', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="number"
              label="Numero"
              aria-label="Numero"
              fullWidth
              disabled={disabled}
              value={values?.number || ''}
              onChange={(event) => onChange('number', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data"
              aria-label="Data"
              value={values?.date || null}
              onChange={(date) => onChange('date', date || null)}
              disabled
              renderInput={(params) => (
                <PmiTextField {...params} id="date" fullWidth helperText="" />
              )}
            />
          </Grid>
        </>
      ) : undefined}
      {values?.withDatiCatastali && !isStruttura ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati catastali</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="section"
              label="Sezione"
              aria-label="Sezione"
              fullWidth
              disabled={disabled}
              value={values?.section?.toUpperCase() || ''}
              onChange={(event) => onChange('section', event.target.value?.toUpperCase())}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="sheet"
              label="Foglio"
              aria-label="Foglio"
              fullWidth
              disabled={disabled}
              value={values?.sheet || ''}
              onChange={(event) => onChange('sheet', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="particle"
              label="Particella"
              aria-label="Particella"
              fullWidth
              disabled={disabled}
              value={values?.particle || ''}
              onChange={(event) => onChange('particle', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="sub"
              label="Subalterno"
              aria-label="Subalterno"
              fullWidth
              disabled={disabled}
              value={values?.sub || ''}
              onChange={(event) => onChange('sub', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ListCategory
              value={values?.cat || ''}
              disabled={disabled}
              onChange={onChangeCategory}
            />
          </Grid>
        </>
      ) : undefined}

      {showERP ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati ERP</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCodeERP"
              label="Codice fiscale ERP"
              aria-label="Codice fiscale ERP"
              fullWidth
              disabled
              value={values?.fiscalCodeERP || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="enteERP"
              label="Ente ERP"
              aria-label="Ente ERP"
              fullWidth
              disabled
              value={values?.enteERP || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="pecEnte"
              label="PEC Ente"
              aria-label="PEC Ente"
              fullWidth
              disabled
              value={values?.pecEnte || ''}
            />
          </Grid>
        </>
      ) : undefined}

      {showOspitante ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati ospitante</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCodeOspitante"
              label="Codice fiscale"
              aria-label="Codice fiscale ospitante"
              fullWidth
              disabled
              value={values?.fiscalCodeOspitante || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="surnameOspitante"
              label="Cognome"
              aria-label="Cognome ospitante"
              fullWidth
              disabled
              value={values?.surnameOspitante || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="nameOspitante"
              label="Nome"
              aria-label="Nome ospitante"
              fullWidth
              disabled
              value={values?.nameOspitante || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data di nascita"
              aria-label="Data di nascita ospitante"
              value={values?.birthDateOspitante || null}
              onChange={() => undefined}
              disabled
              renderInput={(params) => (
                <PmiTextField {...params} id="birthDateOspitante" fullWidth helperText="" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="municipalityResidenceOspitante"
              label="Comune residenza"
              aria-label="Comune residenza ospitante"
              fullWidth
              disabled
              value={values?.municipalityResidenceOspitante || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="addressOspitante"
              label="Indirizzo"
              aria-label="Indirizzo ospitante"
              fullWidth
              disabled
              value={values?.addressOspitante || ''}
            />
          </Grid>
        </>
      ) : undefined}

      {showStruttura ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati struttura</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCodeStruttura"
              label="Codice fiscale"
              aria-label="Codice fiscale struttura"
              fullWidth
              disabled
              value={values?.fiscalCodeStruttura || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="nameStruttura"
              label="Denominazione struttura"
              aria-label="Denominazione struttura"
              fullWidth
              disabled
              value={values?.nameStruttura || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="municipalityResidenceStruttura"
              label="Comune sede"
              aria-label="Comune sede struttura"
              fullWidth
              disabled
              value={values?.municipalityResidenceStruttura || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="addressStruttura"
              label="Indirizzo"
              aria-label="Indirizzo struttura"
              fullWidth
              disabled
              value={values?.addressStruttura || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="pecEnteStruttura"
              label="PEC Ente"
              aria-label="PEC Ente struttura"
              fullWidth
              disabled
              value={values?.pecEnteStruttura || ''}
            />
          </Grid>
        </>
      ) : undefined}

      {showProprietario ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati proprietario</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCodeProprietario"
              label="Codice fiscale"
              aria-label="Codice fiscale proprietario"
              fullWidth
              disabled
              value={values?.fiscalCodeProprietario || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="surnameProprietario"
              label="Cognome"
              aria-label="Cognome proprietario"
              fullWidth
              disabled
              value={values?.surnameProprietario || ''}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="nameProprietario"
              label="Nome"
              aria-label="Nome proprietario"
              fullWidth
              disabled
              value={values?.nameProprietario || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="municipalityResidenceProprietario"
              label="Comune residenza"
              aria-label="Comune residenza proprietario"
              fullWidth
              disabled
              value={values?.municipalityResidenceProprietario || ''}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="addressProprietario"
              label="Indirizzo"
              aria-label="Indirizzo proprietario"
              fullWidth
              disabled
              value={values?.addressProprietario || ''}
            />
          </Grid>
        </>
      ) : undefined}

      {values?.note ? (
        <Grid item xs={12}>
          <PmiTextField
            id="note"
            label="Note"
            aria-label="Note"
            fullWidth
            disabled={disabled}
            value={values?.note || ''}
            onChange={(event) => onChange('note', event.target.value)}
            multiline
            rows={3}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormComponent.displayName = 'Practice Detail - Form - accordion address - form';

const Form = memo(FormComponent);

export default Form;
